import { Injectable, computed, signal } from "@angular/core";
import type { Feature } from "geojson";
import type { FeatureDetailPanelState, OpenFeatureDetailParams } from "./feature-detail-panel.types";

/**
 * Holds the feature currently displayed in the floating feature-detail panel, and the
 * sibling features of its layer for prev/next rotation. Persistence of edited properties is
 * delegated to the `onUpdateProperties` callback supplied by the caller — this service stays
 * agnostic of where features come from (GeoJSON user layer, pipeline layer, in-memory array).
 */
@Injectable({ providedIn: "root" })
export class FeatureDetailPanelService {
  private readonly _state = signal<FeatureDetailPanelState | null>(null);
  readonly state = this._state.asReadonly();

  readonly editable = computed(() => !!this._state()?.onUpdateProperties);
  readonly currentFeature = computed<Feature | null>(() => {
    const state = this._state();
    return state ? (state.features[state.currentIndex] ?? null) : null;
  });

  open(params: OpenFeatureDetailParams): void {
    const lastIndex = Math.max(params.features.length - 1, 0);
    const startIndex = Math.min(Math.max(params.startIndex, 0), lastIndex);
    this._state.set({
      layerName: params.layerName,
      features: params.features,
      currentIndex: startIndex,
      sourceEpsg: params.sourceEpsg ?? 4326,
      onUpdateProperties: params.onUpdateProperties,
    });
  }

  close(): void {
    this._state.set(null);
  }

  next(): void {
    this._state.update((state) =>
      state && state.currentIndex < state.features.length - 1 ? { ...state, currentIndex: state.currentIndex + 1 } : state,
    );
  }

  previous(): void {
    this._state.update((state) => (state && state.currentIndex > 0 ? { ...state, currentIndex: state.currentIndex - 1 } : state));
  }

  updateFeatureProperties(properties: Record<string, unknown>): void {
    const state = this._state();
    const feature = state?.features[state.currentIndex];
    if (!state?.onUpdateProperties || !feature) return;
    feature.properties = properties;
    state.onUpdateProperties(feature, properties);
  }
}
